import { useCallback, useEffect, useRef, useState } from "react";

function getRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function friendlyVoiceError(code) {
  switch (code) {
    case "not-allowed":
    case "service-not-allowed":
      return "Microphone permission denied — allow mic access and try again.";
    case "network":
      return "Voice service unreachable. Check your connection, use Chrome/Edge on HTTPS, or switch to Text.";
    case "audio-capture":
      return "No microphone found. Plug one in or switch to Text.";
    case "language-not-supported":
      return "This language isn’t supported for voice. Switch to Text.";
    default:
      return `Voice error: ${code}. Try again or switch to Text.`;
  }
}

/**
 * Browser speech-to-text via Web Speech API (Chrome, Edge, Safari).
 * Chrome/Edge send audio to a cloud STT endpoint — "network" means that hop failed.
 */
export function useSpeechRecognition({ lang = "en-US", onFinal } = {}) {
  const ctor = getRecognitionCtor();
  const recRef = useRef(null);
  const onFinalRef = useRef(onFinal);
  const listeningRef = useRef(false);
  const retryRef = useRef(0);
  const [supported] = useState(!!ctor);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    onFinalRef.current = onFinal;
  }, [onFinal]);

  useEffect(() => {
    listeningRef.current = listening;
  }, [listening]);

  const bindRecognition = useCallback(() => {
    if (!ctor) return null;
    const rec = new ctor();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = lang;
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      let interimText = "";
      let finalText = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const t = event.results[i][0]?.transcript || "";
        if (event.results[i].isFinal) finalText += t;
        else interimText += t;
      }
      if (interimText) setInterim(interimText.trim());
      if (finalText) {
        const trimmed = finalText.trim();
        setInterim("");
        retryRef.current = 0;
        onFinalRef.current?.(trimmed);
      }
    };

    rec.onerror = (e) => {
      const code = e.error || "unknown";
      // aborted / no-speech are normal end conditions — don't alarm the user.
      if (code === "aborted" || code === "no-speech") {
        setListening(false);
        setInterim("");
        return;
      }

      // Chrome often reports "network" when the recognizer was torn down mid-flight
      // or Google's STT hop blipped — retry once with a fresh instance.
      if (code === "network" && retryRef.current < 1) {
        retryRef.current += 1;
        setListening(false);
        setInterim("");
        try { rec.abort(); } catch (_) { /* noop */ }
        window.setTimeout(() => {
          try {
            const next = bindRecognition();
            if (!next) return;
            recRef.current = next;
            next.start();
            setListening(true);
            setError(null);
          } catch (_) {
            setError(friendlyVoiceError("network"));
            setListening(false);
          }
        }, 350);
        return;
      }

      setError(friendlyVoiceError(code));
      setListening(false);
      setInterim("");
    };

    rec.onend = () => {
      setListening(false);
      setInterim("");
    };

    return rec;
  }, [ctor, lang]);

  useEffect(() => {
    if (!ctor) return undefined;
    recRef.current = bindRecognition();
    return () => {
      try { recRef.current?.abort(); } catch (_) { /* noop */ }
      recRef.current = null;
    };
  }, [ctor, bindRecognition]);

  const start = useCallback(() => {
    if (!ctor || listeningRef.current) return false;
    setError(null);
    setInterim("");
    try {
      // Fresh instance avoids InvalidStateError after a prior stop/abort.
      try { recRef.current?.abort(); } catch (_) { /* noop */ }
      const rec = bindRecognition();
      if (!rec) return false;
      recRef.current = rec;
      rec.start();
      setListening(true);
      return true;
    } catch (e) {
      setError("Could not start microphone — tap again or switch to Text.");
      setListening(false);
      return false;
    }
  }, [ctor, bindRecognition]);

  const stop = useCallback(() => {
    if (!recRef.current) return;
    try { recRef.current.stop(); } catch (e) { /* noop */ }
    setListening(false);
    setInterim("");
  }, []);

  const toggle = useCallback(() => {
    if (listeningRef.current) stop();
    else start();
  }, [start, stop]);

  return {
    supported,
    listening,
    interim,
    error,
    start,
    stop,
    toggle,
    clearError: () => setError(null),
  };
}
